// Snare Constellation (skill 'snare', owner 2026-11-02 'Make them all': surround and collapse). Render-only: reads
// sim.snares (centre, ring radius, orb count and angle, phase, time) and the 'snare' / 'snareSlam' / 'snareCollapse' events;
// decides nothing about gameplay. Starlight look (pale gold, white-hot cores):
//  - the ring: star-orbs pop in one after another around the crowd, hovering and twinkling (crisp cel core + soft halo + a
//    slowly turning four-point glint), threaded into a constellation (each orb to its neighbours and a fainter star polygon)
//    over a thin gold circle drawn on the ground; Starcage adds bars from every orb down to the ground
//  - the slam: every orb fires inward as a converging bolt (crisp gold lightning, a streak trail behind each orb)
//  - the collapse: a gold ring snaps shut on the centre, white pinch star, motes rush in, a burst of sparks and a rising
//    light column, a scorch; Starcage's echo bursts outward as a gold dome
import * as THREE from 'three/webgpu';
import { abs, atan, float, fract, length, mix, smoothstep, step, time, uv, vec2, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { SNARE_SPAWN } from '../core/skillStats';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import type { Lightning } from './lightning';
import { Cards, Decals, Ribbons, clamp01, col, glintLook, haloLook, orbLook, popIn, pushRolled } from './skillsBShared';

const CORE = 0xfffbe8, BODY = 0xffb424, RIM = 0xa84a00, THREAD = 0xffa21c, GLOW = 0xffc030, BOLT = 0xffa818;
const HANG_Y = 1.7;            // orb height above the ground while the ring hangs (m)
const ORB = 1.0, ORB_EVO = 1.15; // orb card size (m)
const MAXS = 4, MAXO = 16;
const TAU = Math.PI * 2;
type Ev<T extends SimEvent['t']> = Extract<SimEvent, { t: T }>;
interface Implode { on: boolean; x: number; z: number; gy: number; r: number; t: number; echo: boolean }

const _e = new THREE.Color(), _c = new THREE.Color();
/** constellation threads: (step to the other orb, width m, brightness) — neighbours, then the fainter star polygon */
const THREADS: readonly (readonly [number, number, number])[] = [[1, 0.13, 1], [2, 0.085, 0.6]];

/** gold circle on the ground: an outer line, a dashed inner ring that turns, a faint fill. a = (alpha, -, -, evolved) */
function ringDecal(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const p = uv().sub(vec2(0.5, 0.5)), d = length(p).mul(2), ang = atan(p.y, p.x).div(Math.PI * 2);
  const line = smoothstep(0.045, 0.0, abs(d.sub(0.93)));
  const dash = step(0.5, fract(ang.mul(40).add(time.mul(0.6)))).mul(smoothstep(0.018, 0.0, abs(d.sub(0.84))));
  const tick = step(0.82, fract(ang.mul(8).sub(time.mul(0.25)))).mul(smoothstep(0.06, 0.0, abs(d.sub(0.98))));
  const fill = smoothstep(0.93, 0.2, d).mul(0.1).add(smoothstep(0.93, 0.88, d).mul(0.12));
  _c.setHex(BODY);
  m.colorNode = mix(vec3(_c.r, _c.g, _c.b).mul(1.3), vec3(1.5, 1.45, 1.25), line.mul(0.22));
  m.opacityNode = line.add(dash.mul(0.7)).add(tick.mul(0.8)).add(fill.mul(float(1).add(a.w))).min(1).mul(a.x);
  return m;
}
/** a single crisp ring line (the implosion snapping shut / the echo shock): white core, gold sheath. a = (alpha, width) */
function lineDecal(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const d = length(uv().sub(vec2(0.5, 0.5))).mul(2), w = a.y, dd = abs(d.sub(float(1).sub(w)));
  _c.setHex(BODY);
  m.colorNode = mix(vec3(_c.r, _c.g, _c.b).mul(1.4), vec3(1.6, 1.55, 1.4), smoothstep(w.mul(0.45), w.mul(0.2), dd));
  m.opacityNode = smoothstep(w, w.mul(0.75), dd).mul(a.x);
  return m;
}

export class SnareFx {
  private orbs: Cards; private halos: Cards; private glints: Cards;
  private threads: Ribbons; private trails: Ribbons;
  private rings: Decals; private lines: Decals;
  private imp: Implode[] = Array.from({ length: 6 }, () => ({ on: false, x: 0, z: 0, gy: 0, r: 1, t: 0, echo: false }));
  private pts = new Float32Array(MAXO * 3); private seen = new Float32Array(MAXO);
  private seg = new Float32Array(6);
  /** QA counters */
  casts = 0; slams = 0; collapses = 0;
  /** hero gesture request: world yaw toward the ring centre and seconds to hold the hands-forward pose */
  aimYaw = 0; hold = 0;

  constructor(scene: THREE.Scene, private fx: Fx, private bolts: Lightning, private glow: GroundGlow, private groundAt: (x: number, z: number) => number, private burn: (x: number, z: number, r: number) => void) {
    this.halos = new Cards(scene, MAXS * MAXO, haloLook(GLOW, 0.95), 4);
    this.orbs = new Cards(scene, MAXS * MAXO, orbLook(CORE, BODY, RIM, 1.9), 6);
    this.glints = new Cards(scene, MAXS * MAXO, glintLook(0xffe7a0, 1.1), 7);
    this.threads = new Ribbons(scene, MAXS * MAXO * 3, 2, 5);
    this.trails = new Ribbons(scene, MAXS * MAXO, 2, 5);
    this.rings = new Decals(scene, MAXS, ringDecal, 1);
    this.lines = new Decals(scene, 8, lineDecal, 2);
  }

  event(ev: SimEvent, sim: Sim): void {
    const fx = this.fx;
    if (ev.t === 'snare') {
      this.casts++;
      const gy = this.groundAt(ev.x, ev.z);
      fx.rings.add(ev.x, gy + 0.12, ev.z, ev.r * 1.05, 0.35, BODY, 1.6, 0.6);
      fx.stars.add(ev.x, gy + 0.6, ev.z, 1.4, 0.18, 0xfff2c0, 2);
      const p = sim.player; this.aimYaw = Math.atan2(ev.x - p.x, ev.z - p.z);
    } else if (ev.t === 'snareSlam') {
      this.slams++;
      const s = sim.snares.find((q) => q.alive && q.id === ev.id); if (!s) return;
      const gy = this.groundAt(s.x, s.z), cy = gy + 1.1;
      for (let k = 0; k < s.n; k++) {
        const a = s.a0 + (k / s.n) * TAU, px = s.x + Math.cos(a) * s.r, pz = s.z + Math.sin(a) * s.r, py = this.groundAt(px, pz) + HANG_Y;
        // converging bolt: crisp gold lightning from the orb straight into the centre
        this.bolts.add([px, py, pz, (px + s.x) / 2, (py + cy) / 2 + 0.25, (pz + s.z) / 2, s.x, cy, s.z], BOLT, ev.evolved ? 0.42 : 0.36, 0.24, 0.35);
        fx.stars.add(px, py, pz, 1.3, 0.1, 0xfff4cc, 2.4);
      }
      this.glow.flash(s.x, s.z, s.r * 0.9, GLOW, 0.5, 0.25);
      const p = sim.player; this.aimYaw = Math.atan2(s.x - p.x, s.z - p.z); this.hold = 0.35;
    } else if (ev.t === 'snareCollapse') {
      this.collapses++;
      this.implode(ev);
    }
  }

  private implode(ev: Ev<'snareCollapse'>): void {
    const fx = this.fx, gy = this.groundAt(ev.x, ev.z), y = gy + 1.1, r = ev.br;
    const slot = this.imp.find((q) => !q.on) ?? this.imp[0];
    Object.assign(slot, { on: true, x: ev.x, z: ev.z, gy, r, t: 0, echo: ev.echo });
    if (ev.echo) { // Starcage echo: a gold dome bursting outward
      fx.blast?.novaWave(ev.x, gy + 0.2, ev.z, r, true);
      fx.stars.add(ev.x, y, ev.z, r * 0.9, 0.2, 0xffe08a, 1.8);
      for (let k = 0; k < 20; k++) { const a = (k / 20) * TAU, sp = r * 3; fx.particles.emitDir(ev.x + Math.cos(a) * 0.6, gy + 0.3, ev.z + Math.sin(a) * 0.6, Math.cos(a) * sp, 0.8 + Math.random(), Math.sin(a) * sp, k % 2 ? 0xffd060 : 0xfff2c0, 0.32, 0.36, 3); }
      fx.shake = Math.min(1, fx.shake + 0.25);
      this.glow.flash(ev.x, ev.z, r * 1.5, GLOW, 0.9, 0.4);
      return;
    }
    // the pinch: white star, tight gold flash, motes rushing in, then sparks out and a rising column of light
    fx.stars.add(ev.x, y, ev.z, 2.4, 0.16, 0xffffff, 2.8);
    fx.stars.add(ev.x, y, ev.z, r * 1.3, 0.26, 0xffc44a, 1.7);
    fx.flashes.add(ev.x, y, ev.z, r * 0.35, 0.16, 0xffd060, 1.3);
    fx.rings.add(ev.x, gy + 0.14, ev.z, r * 1.25, 0.42, 0xffc040, 2.6, 0.7);
    for (let k = 0; k < 28; k++) { // motes sucked into the centre
      const a = Math.random() * TAU, R = r * (0.9 + Math.random() * 0.8), life = 0.16 + Math.random() * 0.08, hy = 0.3 + Math.random() * 2.2;
      fx.particles.emitDir(ev.x + Math.cos(a) * R, gy + hy, ev.z + Math.sin(a) * R, -Math.cos(a) * R / life, (y - gy - hy) / life, -Math.sin(a) * R / life, k % 3 ? 0xffd870 : 0xffffff, 0.26, life, 3);
    }
    for (let k = 0; k < 18; k++) { const a = Math.random() * TAU, sp = 5 + Math.random() * 7; fx.particles.emitDir(ev.x, y, ev.z, Math.cos(a) * sp, 2 + Math.random() * 6, Math.sin(a) * sp, k % 3 ? 0xffc040 : 0xfff6dc, 0.22, 0.4, 3); }
    for (let k = 0; k < 10; k++) fx.particles.emitDir(ev.x + (Math.random() - 0.5) * 0.5, gy + 0.3 + k * 0.45, ev.z + (Math.random() - 0.5) * 0.5, 0, 7 + Math.random() * 3, 0, k % 2 ? 0xfff2c0 : 0xffd050, 0.55 - k * 0.025, 0.35, 3.2); // the light column
    for (let k = 0; k < 6; k++) { const a = (k / 6) * TAU + Math.random() * 0.5, L = r * (0.7 + Math.random() * 0.4); this.bolts.add([ev.x, y, ev.z, ev.x + Math.cos(a) * L * 0.5, (y + gy) / 2 + 0.3, ev.z + Math.sin(a) * L * 0.5, ev.x + Math.cos(a) * L, gy + 0.25, ev.z + Math.sin(a) * L], BOLT, 0.2, 0.18, 0.4); }
    if (FLASH.dim >= 1) fx.lights.flash(ev.x, y, ev.z, 0xffc84a, 34, 0.3, r * 3);
    fx.shake = Math.min(1, fx.shake + 0.3);
    this.glow.flash(ev.x, ev.z, r * 1.6, GLOW, 1, 0.4);
    this.burn(ev.x, ev.z, r * 0.55);
  }

  update(sim: Sim, dt: number, cam: THREE.Camera): void {
    const camQ = cam.quaternion, camP = cam.position;
    this.hold = Math.max(0, this.hold - dt);
    this.orbs.begin(); this.halos.begin(); this.glints.begin(); this.threads.begin(); this.trails.begin(); this.rings.begin(); this.lines.begin();
    const P = this.pts, S = this.seen, tm = performance.now() / 1000;
    for (const s of sim.snares) {
      if (!s.alive || s.phase >= 2) continue;
      const gy = this.groundAt(s.x, s.z), n = Math.min(s.n, MAXO), fly = s.phase === 1 ? clamp01((s.t - s.hang) / s.fly) : 0, e = fly * fly;
      // the ground circle (fades in as the orbs gather, out as they fly)
      const ra = clamp01(s.t / 0.25) * (1 - fly);
      this.rings.push(s.x, gy + 0.07, s.z, s.r * 2.15, s.a0, ra * 0.9, 0, 0, s.evo ? 1 : 0);
      for (let k = 0; k < n; k++) {
        const a = s.a0 + (k / n) * TAU, age = s.t - (k / n) * SNARE_SPAWN;
        S[k] = age;
        const rx = s.x + Math.cos(a) * s.r, rz = s.z + Math.sin(a) * s.r, ry = this.groundAt(rx, rz) + HANG_Y + Math.sin(tm * 3 + k * 1.7) * 0.12;
        const x = rx + (s.x - rx) * e, z = rz + (s.z - rz) * e, y = ry + (gy + 1.1 - ry) * e;
        P[k * 3] = x; P[k * 3 + 1] = y; P[k * 3 + 2] = z;
        if (age < 0) continue;
        const sz = (s.evo ? ORB_EVO : ORB) * popIn(age, 0.16) * (1 + fly * 0.35), tw = 0.5 + 0.5 * Math.sin(tm * 9 + k * 2.3);
        this.halos.push(x, y, z, sz * 3.2, camQ, 1);
        this.orbs.push(x, y, z, sz, camQ, 1, s.phase === 1 ? 1 : 0.25 + 0.25 * tw);
        pushRolled(this.glints, x, y, z, sz * (2.4 + tw * 0.8), camQ, 0.55 + 0.45 * tw, 0, tm * 1.3 + k);
        if (s.phase === 1) { // streak behind each converging orb
          const sg = this.seg; sg[0] = rx; sg[1] = ry; sg[2] = rz; sg[3] = x; sg[4] = y; sg[5] = z;
          this.trails.strip(sg, 2, 0.08, sz * 0.75, 0, 0.95, col(_e, BODY, 1.2), col(_c, 0xffffff, 1.5), 0.4, camP, 0.6);
        }
        if (Math.random() < 0.12) this.fx.particles.emitDir(x, y, z, (Math.random() - 0.5) * 0.6, -0.4, (Math.random() - 0.5) * 0.6, 0xffe08a, 0.12, 0.5, 2.6); // falling star dust
      }
      if (s.phase === 1) { // the converging stars feed a small sun at the centre
        const cs = 0.5 + s.br * 0.5 * e;
        this.halos.push(s.x, gy + 1.1, s.z, cs * 3, camQ, 1);
        this.orbs.push(s.x, gy + 1.1, s.z, cs, camQ, 1, 0.6 + 0.4 * e);
      }
      if (s.phase !== 0) continue;
      // constellation threads: each orb to its neighbour (drawn as the second one appears) and a fainter star polygon
      const pulse = 0.7 + 0.3 * Math.sin(tm * 5);
      for (let k = 0; k < n; k++) {
        for (const [step2, w, k2] of THREADS) {
          if (step2 === 2 && n < 5) continue;
          const j = (k + step2) % n, born = Math.max(S[k], S[j]);
          if (born < 0) continue;
          const u = clamp01(born / 0.12), sg = this.seg;
          sg[0] = P[k * 3]; sg[1] = P[k * 3 + 1]; sg[2] = P[k * 3 + 2];
          sg[3] = P[k * 3] + (P[j * 3] - P[k * 3]) * u; sg[4] = P[k * 3 + 1] + (P[j * 3 + 1] - P[k * 3 + 1]) * u; sg[5] = P[k * 3 + 2] + (P[j * 3 + 2] - P[k * 3 + 2]) * u;
          this.threads.strip(sg, 2, w, w, k2 * pulse, k2 * pulse, col(_e, THREAD, 1.25), col(_c, 0xfff6dc, 1.6), 0.4, camP);
        }
        if (s.evo && S[k] >= 0) { // Starcage bars: light from each orb down into the ground
          const sg = this.seg, x = P[k * 3], z = P[k * 3 + 2];
          sg[0] = x; sg[1] = this.groundAt(x, z) + 0.05; sg[2] = z; sg[3] = x; sg[4] = P[k * 3 + 1]; sg[5] = z;
          this.threads.strip(sg, 2, 0.16, 0.06, 0.15, 0.8 * pulse, col(_e, THREAD, 1.1), col(_c, 0xfff4d0, 1.3), 0.4, camP);
        }
      }
    }
    // the implosion ring snapping shut, then a thin shock line out (echo: a wide outward ring)
    for (const q of this.imp) {
      if (!q.on) continue;
      q.t += dt;
      if (q.echo) {
        const u = clamp01(q.t / 0.35); if (u >= 1) { q.on = false; continue; }
        this.lines.push(q.x, q.gy + 0.09, q.z, q.r * 2 * (0.3 + 0.9 * u), 0, (1 - u) * (1 - u), 0.06);
        continue;
      }
      if (q.t < 0.1) { const u = q.t / 0.1; this.lines.push(q.x, q.gy + 0.09, q.z, q.r * 2.4 * (1 - u * u) + 0.2, 0, 0.9, 0.08); }
      else if (q.t < 0.45) { const u = (q.t - 0.1) / 0.35; this.lines.push(q.x, q.gy + 0.09, q.z, q.r * 2.6 * (0.2 + u), 0, (1 - u) * 0.9, 0.035); }
      else { q.on = false; continue; }
      // the sun flares, then is crushed to a point; a column of light shoots up out of it
      const cy = q.gy + 1.1, sun = q.t < 0.06 ? q.r * (0.55 + 0.6 * q.t / 0.06) : q.r * 1.15 * Math.max(0, 1 - (q.t - 0.06) / 0.18) ** 2;
      if (sun > 0.02) { this.halos.push(q.x, cy, q.z, sun * 2.6, camQ, 1); this.orbs.push(q.x, cy, q.z, sun, camQ, 1, 1); }
      const cu = clamp01(q.t / 0.35), sg = this.seg;
      sg[0] = q.x; sg[1] = q.gy + 0.1; sg[2] = q.z; sg[3] = q.x; sg[4] = q.gy + 1.5 + 7 * Math.sqrt(cu); sg[5] = q.z;
      this.trails.strip(sg, 2, q.r * 0.5 * (1 - cu), q.r * 0.18 * (1 - cu), (1 - cu) * 0.95, (1 - cu) * 0.5, col(_e, BODY, 1.25), col(_c, 0xffffff, 1.7), 0.5, camP);
    }
    this.orbs.end(); this.halos.end(); this.glints.end(); this.threads.end(); this.trails.end(); this.rings.end(); this.lines.end();
  }

  /** soft gold pools under the hanging orbs (groundGlow.ts) */
  glowPools(g: GroundGlow, sim: Sim): void {
    for (const s of sim.snares) {
      if (!s.alive || s.phase >= 2) continue;
      const fly = s.phase === 1 ? clamp01((s.t - s.hang) / s.fly) : 0, e = fly * fly, n = Math.min(s.n, MAXO);
      for (let k = 0; k < n; k++) {
        if (s.t < (k / n) * SNARE_SPAWN) continue;
        const a = s.a0 + (k / n) * TAU, x = s.x + Math.cos(a) * s.r * (1 - e), z = s.z + Math.sin(a) * s.r * (1 - e);
        g.blob(x, this.groundAt(x, z) + HANG_Y, z, 1.2, GLOW, 0.55);
      }
      if (fly > 0) g.blob(s.x, this.groundAt(s.x, s.z) + 1.1, s.z, s.br * fly, GLOW, 0.8 * fly);
    }
  }

  clear(): void { this.orbs.clear(); this.halos.clear(); this.glints.clear(); this.threads.clear(); this.trails.clear(); this.rings.clear(); this.lines.clear(); for (const q of this.imp) q.on = false; this.hold = 0; }
}
